import { useSyncExternalStore } from 'react';

/**
 * Mobile vs desktop shell mode (QF WorkspaceShell pattern).
 *
 * Auto rule: (max-width: 768px) and (pointer: coarse).
 * Override: localStorage preferredLayout auto | mobile | desktop
 * (same key as legacy layout preference so existing user choices still apply).
 */

export type ShellOverride = 'auto' | 'mobile' | 'desktop';
export type ShellMode = 'mobile' | 'desktop';

/** Back-compat with deviceDetection / MobileSettings. */
export const SHELL_OVERRIDE_KEY = 'preferredLayout';

export const MOBILE_QUERY = '(max-width: 768px) and (pointer: coarse)';

const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

/** Pure resolution — exported for unit tests without a DOM. */
export function shellModeFor(
  queryMatches: boolean,
  override: ShellOverride,
): ShellMode {
  if (override === 'mobile') return 'mobile';
  if (override === 'desktop') return 'desktop';
  return queryMatches ? 'mobile' : 'desktop';
}

export function getShellOverride(): ShellOverride {
  try {
    const v = localStorage.getItem(SHELL_OVERRIDE_KEY);
    if (v === 'mobile' || v === 'desktop' || v === 'auto') return v;
  } catch {
    /* private mode */
  }
  return 'auto';
}

/**
 * Sole writer for shell override. Never set preferredLayout via raw localStorage.
 */
export function setShellOverride(v: ShellOverride): void {
  try {
    if (v === 'auto') {
      localStorage.removeItem(SHELL_OVERRIDE_KEY);
    } else {
      localStorage.setItem(SHELL_OVERRIDE_KEY, v);
    }
  } catch {
    /* private mode */
  }
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  if (typeof window === 'undefined') {
    return () => {
      listeners.delete(cb);
    };
  }
  const mq = window.matchMedia(MOBILE_QUERY);
  mq.addEventListener('change', cb);
  return () => {
    listeners.delete(cb);
    mq.removeEventListener('change', cb);
  };
}

function getSnapshot(): ShellMode {
  if (typeof window === 'undefined') return 'desktop';
  return shellModeFor(
    window.matchMedia(MOBILE_QUERY).matches,
    getShellOverride(),
  );
}

function getServerSnapshot(): ShellMode {
  return 'desktop';
}

export function useShellMode(): ShellMode {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
